'use client';

import { FC, useContext, useState } from 'react';
import {
  EditEntityInfoDialogProps,
  EditEntityInfoTab,
} from './EditEntityInfoDialog.types';
import { Dialog, Tabs, TabsItem } from '@/_components';
import { DetailsForm } from './components/DetailsForm';
import { SettingsForm } from './components/SettingsForm';
import { FirmRoleSettingsForm } from './components/FirmRoleSettingsForm';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const EditEntityInfoDialog: FC<EditEntityInfoDialogProps> = ({
  isOpen,
  onClose,
  company,
}) => {
  const { preferences } = useContext(FirmSettingsContext);
  const { permissions } = useContext(UserContext);

  const [active, setActive] = useState<EditEntityInfoTab>('details');

  const hasEditEntityPermission: boolean = getHasPermission(
    PermissionsMapEnum['company.edit'],
    permissions,
    company?.id,
  );

  const tabItems: Array<TabsItem> = [
    {
      title: 'Details',
      onClick: () => setActive('details'),
      isActive: active === 'details',
    },
    {
      title: 'Settings',
      onClick: () => setActive('settings'),
      isActive: active === 'settings',
    },
    ...(preferences.use_firm_roles && hasEditEntityPermission
      ? [
          {
            title: 'Firm Roles',
            onClick: () => setActive('firmRoles'),
            isActive: active === 'firmRoles',
          },
        ]
      : []),
  ];

  const onCloseAndReset = () => {
    setActive('details');
    onClose();
  };

  const activeTab: Record<EditEntityInfoTab, React.ReactNode> = {
    details: <DetailsForm company={company} onClose={onCloseAndReset} />,
    firmRoles: <FirmRoleSettingsForm company={company} onClose={onClose} />,
    settings: <SettingsForm company={company} />,
  };

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onCloseAndReset}
        title="Edit Entity Information"
        isOverflowing={true}
      >
        <Dialog.Content>
          <Tabs items={tabItems} noPadding={true}>
            {activeTab[active]}
          </Tabs>
        </Dialog.Content>
      </Dialog>
    </>
  );
};
